import React, { useEffect, useState } from 'react';

/* ── Icons ─────────────────────────────────────────────────────────────── */
const SunIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="4" />
    <line x1="12" y1="2"  x2="12" y2="5" />
    <line x1="12" y1="19" x2="12" y2="22" />
    <line x1="4.22"  y1="4.22"  x2="6.34"  y2="6.34" />
    <line x1="17.66" y1="17.66" x2="19.78" y2="19.78" />
    <line x1="2"  y1="12" x2="5"  y2="12" />
    <line x1="19" y1="12" x2="22" y2="12" />
    <line x1="4.22"  y1="19.78" x2="6.34"  y2="17.66" />
    <line x1="17.66" y1="6.34"  x2="19.78" y2="4.22" />
  </svg>
);

const MoonIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
  </svg>
);

const PersonIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const GridIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3"  y="3"  width="7" height="7" rx="1" />
    <rect x="14" y="3"  width="7" height="7" rx="1" />
    <rect x="3"  y="14" width="7" height="7" rx="1" />
    <rect x="14" y="14" width="7" height="7" rx="1" />
  </svg>
);

const TimelineIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <line x1="8" y1="6"  x2="21" y2="6" />
    <line x1="8" y1="12" x2="21" y2="12" />
    <line x1="8" y1="18" x2="21" y2="18" />
    <circle cx="3.5" cy="6"  r="1.5" fill="currentColor" stroke="none" />
    <circle cx="3.5" cy="12" r="1.5" fill="currentColor" stroke="none" />
    <circle cx="3.5" cy="18" r="1.5" fill="currentColor" stroke="none" />
  </svg>
);

const CodeIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="16 18 22 12 16 6" />
    <polyline points="8 6 2 12 8 18" />
  </svg>
);

const TerminalDockIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="4 17 10 11 4 5" />
    <line x1="12" y1="19" x2="20" y2="19" />
  </svg>
);

/* ── Data ──────────────────────────────────────────────────────────────── */
const NAV_ITEMS = [
  { id: 'about',    label: 'About',    Icon: PersonIcon },
  { id: 'projects', label: 'Projects', Icon: GridIcon },
  { id: 'timeline', label: 'Timeline', Icon: TimelineIcon },
  { id: 'skills',   label: 'Skills',   Icon: CodeIcon },
];

const getStoredTheme = () => {
  const s = localStorage.getItem('portfolio-theme');
  return s === 'light' || s === 'dark' ? s
    : (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');
};

/* ── Component ─────────────────────────────────────────────────────────── */
const MobileDock = ({ onOpenTerminal }) => {
  const [activeSection, setActiveSection] = useState('about');
  const [theme, setTheme] = useState(getStoredTheme);

  /* sync theme to DOM + localStorage */
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('portfolio-theme', theme);
  }, [theme]);

  const cycleTheme = () =>
    setTheme(prev => prev === 'light' ? 'dark' : 'light');

  /* IntersectionObserver — track which section is in view */
  useEffect(() => {
    const sectionEls = NAV_ITEMS
      .map(s => document.getElementById(s.id))
      .filter(Boolean);

    const visible = new Map(sectionEls.map(el => [el.id, false]));

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(e => visible.set(e.target.id, e.isIntersecting));
        const first = NAV_ITEMS.find(s => visible.get(s.id));
        if (first) setActiveSection(first.id);
      },
      { rootMargin: '-20% 0px -60% 0px', threshold: 0 }
    );

    sectionEls.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const scrollTo = id =>
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <nav className="mobile-dock" aria-label="Mobile navigation">
      {/* Theme bubble */}
      <button
        className="dock-theme-bubble"
        onClick={cycleTheme}
        aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} theme`}
      >
        {theme === 'light' ? <SunIcon /> : <MoonIcon />}
      </button>

      {/* Nav items */}
      {NAV_ITEMS.map(({ id, label, Icon }) => (
        <button
          key={id}
          className={`dock-item${activeSection === id ? ' active' : ''}`}
          onClick={() => scrollTo(id)}
          aria-label={`Go to ${label}`}
        >
          <span className="dock-icon"><Icon /></span>
          <span className="dock-label">{label}</span>
          {activeSection === id && <span className="dock-dot" aria-hidden="true" />}
        </button>
      ))}

      {/* Terminal */}
      <button
        className="dock-item"
        onClick={onOpenTerminal}
        aria-label="Open terminal"
      >
        <span className="dock-icon"><TerminalDockIcon /></span>
        <span className="dock-label">Terminal</span>
      </button>
    </nav>
  );
};

export default MobileDock;
